import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['filterHost', 'list', 'outputHost'];
  static values  = { notes: Array };

  connect() {
    this.filterEl = this.filterHostTarget.querySelector('input, [contenteditable="true"]');
    this.outputEl = this.outputHostTarget.querySelector('textarea, [role="textbox"]');

    if (!this.filterEl) this.filterEl = this.element.querySelector('input');
    if (!this.outputEl) this.outputEl = this.element.querySelector('textarea');

    this._all = Array.isArray(this.notesValue) ? this.notesValue.slice() : [];
    this.selectedNotes = new Set();
    this._renderList(this._all);
    this._setupEditorChangeHandler();

    // events
    if (this.filterEl) {
      this._onFilter = this.onFilter.bind(this);
      this.filterEl.addEventListener('input', this._onFilter);
    }

    this._onListClick = (e) => this._handlePick(e);
    this.listTarget.addEventListener('click', this._onListClick);

    this._onListKeydown = (e) => this._handleKeyboard(e);
    this.listTarget.addEventListener('keydown', this._onListKeydown);
  }

  disconnect() {
    if (this.filterEl && this._onFilter) this.filterEl.removeEventListener('input', this._onFilter);
    if (this._onListClick) this.listTarget.removeEventListener('click', this._onListClick);
    if (this._onListKeydown) this.listTarget.removeEventListener('keydown', this._onListKeydown);
    if (this.outputEl && this.changeHandler) {
      this.outputEl.removeEventListener('input', this.changeHandler);
      if (this.outputEl.tagName === 'TRIX-EDITOR') {
        this.outputEl.removeEventListener('trix-change', this.changeHandler);
      }
    }
  }

  // === UI ===
  _renderList(items) {
    const ul = document.createElement('ul');
    ul.className = 'list-disc pl-20 space-y-0';

    items.forEach((text, idx) => {
      const li = document.createElement('li');
      li.className = 'cursor-pointer hover:bg-black-50 rounded-6 -ml-4 px-4 py-2';
      li.tabIndex = 0;
      li.setAttribute('role', 'option');
      li.dataset.index = String(idx);
      li.textContent = text;
      
      if (this.selectedNotes.has(text)) {
        li.classList.add('text-black-400', 'pointer-events-none');
      }
      
      ul.appendChild(li);
    });

    this.listTarget.innerHTML = '';
    this.listTarget.appendChild(ul);

    if (items.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'text-black-400 text-paragraph-s py-8';
      empty.textContent = 'No matches';
      this.listTarget.appendChild(empty);
    }
  }

  // === Filtering ===
  onFilter() {
    const q = (this.filterEl?.value || '').trim().toLowerCase();
    if (!q) {
      this._renderList(this._all);
      return;
    }
    const filtered = this._all.filter((t) => t.toLowerCase().includes(q));
    this._renderList(filtered);
  }

  // === Pick handlers ===
  _handlePick(e) {
    const li = e.target.closest('li[role="option"]');
    if (!li) return;
    const text = li.textContent || '';
    
    if (this.selectedNotes.has(text)) return;

    this.selectedNotes.add(text);
    li.classList.add('text-black-400', 'pointer-events-none');

    this._insertIntoOutput(text);
  }

  _handleKeyboard(e) {
    const { key } = e;
    const focusable = Array.from(this.listTarget.querySelectorAll('li[role="option"]'));
    if (focusable.length === 0) return;

    const idx = focusable.indexOf(document.activeElement);

    if (key === 'ArrowDown') {
      e.preventDefault();
      const next = focusable[Math.min(focusable.length - 1, Math.max(0, idx + 1))];
      next?.focus();
    } else if (key === 'ArrowUp') {
      e.preventDefault();
      const prev = focusable[Math.max(0, idx - 1)];
      prev?.focus();
    } else if (key === 'Enter') {
      e.preventDefault();
      const li = document.activeElement?.closest?.('li[role="option"]');
      if (li) {
        const text = li.textContent || '';
        if (this.selectedNotes.has(text)) return;

        this.selectedNotes.add(text);
        li.classList.add('text-black-400', 'pointer-events-none');

        this._insertIntoOutput(text);
      }
    }
  }

  _insertIntoOutput(text) {
    if (!this.outputEl) return;
    const current = this._getOutputText();
    const textEl = `<ul><li>${text}</li></ul>`;
    const next = current ? `${current}${textEl}` : textEl;
    this._setOutputText(next);

    if (this.outputEl.setSelectionRange) {
      const end = next.length;
      this.outputEl.focus();
      this.outputEl.setSelectionRange(end, end);
    } else {
      this.outputEl.focus();
    }
  }

  _getOutputText() {
    if (['TEXTAREA', 'TRIX-EDITOR'].includes(this.outputEl.tagName)) return this.outputEl.value || '';
    return this.outputEl.textContent || '';
  }

  _setOutputText(v) {
    if (['TEXTAREA', 'TRIX-EDITOR'].includes(this.outputEl.tagName)) {
      this.outputEl.value = v;
      this.outputEl.dispatchEvent(new Event('input', { bubbles: true }));
    } else {
      this.outputEl.textContent = v;
    }
  }

  _getCurrentItems() {
    const q = (this.filterEl?.value || '').trim().toLowerCase();
    if (!q) return this._all;
    return this._all.filter((t) => t.toLowerCase().includes(q));
  }

  _setupEditorChangeHandler() {
    if (!this.outputEl) return;

    this.changeHandler = () => {
      const currentText = this._getOutputText();
      
      Array.from(this.selectedNotes).forEach(note => {
        if (!currentText.includes(note)) {
          this.selectedNotes.delete(note);
        }
      });
      
      this._renderList(this._getCurrentItems());
    };

    if (this.outputEl.tagName === 'TRIX-EDITOR') {
      this.outputEl.addEventListener('trix-change', this.changeHandler);
    } else {
      this.outputEl.addEventListener('input', this.changeHandler);
    }
  }
}
